import { useState } from 'react'
import { DataTable, DataTablePFSEvent } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { OverlayPanel } from 'primereact/overlaypanel'
import { InputText } from 'primereact/inputtext'
import { TabPanel, TabView } from 'primereact/tabview'
import { classNames } from 'primereact/utils'
import moment from 'moment'

// local imports
import 'pages/Tracing.scss'
import warningImage from 'assets/images/warning-image.svg'
import noRecordImg from 'assets/icons/no-content.png'
import TracingService from 'service/TracingService'
import {
  CampusServiceTypes,
  ICampusServiceReservations,
  ITrackingMultiUpdateResReq,
  ITrackingUpdateResReq,
} from 'types/TrackingType'
import NotificationDialog from 'components/ui/NotificationDialog'
import useReservationTable from './hooks/use-reservation-table'
import { TableChip, TableTextButton } from './styled'

interface ComponentProps {
  data: ICampusServiceReservations[] | undefined
  selectedService: CampusServiceTypes[]
  getTableData: () => void
  loading: boolean
  startDate: Date
  endDate: Date
}

export default function TracingTable({
  data,
  selectedService,
  getTableData,
  startDate,
  endDate,
  loading,
}: ComponentProps) {
  const {
    exportExcel,
    globalFilterValue,
    handleTabChange,
    hasItems,
    multiNotification,
    notification,
    onGlobalFilterChange,
    selectedRow,
    setMultiNotification,
    setNotification,
    setSelectedRow,
    showExcel,
    activeTableData,
    canceledTableData,
    tabs,
    t,
    isCameOptions,
    addToExcel,
    whichTabData,
    tabIndex,
    filters,
    activeBlockList,
    activeFloorList,
    canceledBlockList,
    canceledFloorList,
  } = useReservationTable(data, selectedService)

  /* Custom Pagination */
  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(10)
  const handlePageChange = (e: DataTablePFSEvent) => {
    setFirst(e.first)
    setRows(e.rows)
  }

  /* IsCame Checkbox Handle Change */
  const onIsCameChange = (e: CheckboxChangeParams, rowData: ICampusServiceReservations) => {
    let reqParam: ITrackingUpdateResReq = {
      id: rowData.id,
      reservationStatusId: !!e.checked ? 2 : 1,
      campusServiceType: rowData.campusServiceType,
      campusServiceSubType: rowData.campusServiceSubType,
    }

    TracingService.updateReservationStatus(reqParam).then((_res) => {
      getTableData()
    })
  }

  /* IsCancel Button Handle Change */
  const handleConfirmIsCanceled = (rowData: ICampusServiceReservations | undefined) => {
    if (!rowData) return
    const reqParam: ITrackingUpdateResReq = {
      id: rowData.id,
      reservationStatusId: 3,
      campusServiceType: rowData.campusServiceType,
      campusServiceSubType: rowData.campusServiceSubType,
    }
    TracingService.updateReservationStatus(reqParam).then((_res) => {
      getTableData()
      setNotification(false)
    })
  }

  /* Handle Multiple IsCancel Update */
  const cancelableRows = activeTableData
    ?.filter((dt) => dt.reservationStatusId !== 2)
    .map((dt) => ({
      id: dt.id,
      campusServiceType: dt.campusServiceType,
      campusServiceSubType: dt.campusServiceSubType,
    }))
  const selectedCancelableRows = cancelableRows?.splice(first, rows) || []
  const handleConfirmMultiIsCanceled = () => {
    if (!selectedCancelableRows.length) return
    const reqParam: ITrackingMultiUpdateResReq = {
      campusServiceReservations: selectedCancelableRows,
      reservationStatusId: 3,
    }
    TracingService.updateMultiResCancel(reqParam).then((_res) => {
      getTableData()
      setMultiNotification(false)
    })
  }

  /* ############# Table Templates #############  */
  const tableHeaderTemplate = () => {
    return (
      <div className="table-header">
        <div className="d-flex justify-content-between">
          {/* Search Input */}
          <div className="d-flex flex-row">
            <div className="me-3">
              <span className={classNames('p-input-icon-left', globalFilterValue ? 'p-input-icon-right' : '')}>
                <i className="pi pi-user" />
                <InputText
                  className="of-name-search"
                  style={{ width: '310px' }}
                  value={globalFilterValue}
                  onChange={(e) => onGlobalFilterChange(e.target.value)}
                  placeholder={t('input-texts.search-someone')}
                />
                {!!globalFilterValue && (
                  <i className="pi pi-times" style={{ cursor: 'pointer' }} onClick={() => onGlobalFilterChange('')} />
                )}
              </span>
            </div>
          </div>
          {/* Excel Export Button */}
          <div className="d-flex align-items-center">
            <div
              className="d-flex"
              style={{ cursor: 'pointer' }}
              onMouseOver={(e) => showExcel(e)}
              onMouseLeave={() => addToExcel.current?.hide()}
              onClick={exportExcel}>
              <i className="pi pi-file-excel excelIcon" style={{ fontSize: '25px' }}></i>
            </div>
            <OverlayPanel ref={addToExcel} className="add-excel-panel">
              <div className="add-excel-text">
                <p>{t('overlaypanel-texts.export-excel')}</p>
              </div>
            </OverlayPanel>
          </div>
        </div>
      </div>
    )
  }

  const dateBodyTemplate = (rowData: ICampusServiceReservations) => {
    let formatDate = moment(rowData.reservationStartHour).format('DD/MM/YYYY')
    return <span>{formatDate}</span>
  }

  const reasonBodyTemplate = (_rowData: ICampusServiceReservations) => {
    const lookup = { 1: 'visitor.doctor', 2: 'visitor.coiffeur', 3: 'visitor.gym', 4: 'visitor.lactation' }
    return <TableChip label={t(lookup[_rowData.campusServiceType])} />
  }

  const resDescBodyTemplate = (_rowData: ICampusServiceReservations) => {
    if (_rowData.campusServiceSubType === 1) return <span className="fw-bold">{t('visitor.personal-work')}</span>
    else if (_rowData.campusServiceSubType === 2)
      return (
        <span>
          <span className="fw-bold">{t('visitor.special-lesson')}: </span>
          <br />
          {_rowData?.servicePersonName} {_rowData?.servicePersonSurname}
        </span>
      )
    else if (_rowData.campusServiceSubType === 3)
      return (
        <span>
          <span className="fw-bold">{t('visitor.group-lesson')}: </span>
          <br />
          <span>{_rowData?.placeOfServiceName}</span>
        </span>
      )
    else return '---'
  }

  const resPlaceTemplate = (_rowData: ICampusServiceReservations) => {
    return (
      <span style={{ fontSize: '13px' }}>
        {_rowData.locationName} <br /> <span>{_rowData.placeOfServiceName}</span>
      </span>
    )
  }

  const resPersonTemplate = (_rowData: ICampusServiceReservations) => {
    return (
      <span>
        {_rowData.servicePersonId ? (
          <>
            {_rowData.servicePersonName} {_rowData.servicePersonSurname}
          </>
        ) : (
          '---'
        )}
      </span>
    )
  }

  const cancelDateBodyTemplate = (rowData: ICampusServiceReservations) => {
    if (!rowData.statusUpdateDate) return <span> --- </span>
    const formatDate = moment(rowData.statusUpdateDate).format('DD/MM/YYYY HH:mm:ss')
    return <span>{formatDate}</span>
  }

  const timeBodyTemplate = (rowData: ICampusServiceReservations) => {
    const startTime = moment(rowData.reservationStartHour).format('HH:mm')
    const endTime = moment(rowData.reservationEndHour).format('HH:mm')
    return (
      <span>
        {startTime} - {endTime}
      </span>
    )
  }

  const isCameBodyTemplate = (rowData: ICampusServiceReservations) => {
    const today = moment(new Date()).toDate()
    const disabled = moment(today).isBefore(rowData.reservationEndHour)
    return (
      <Checkbox
        disabled={disabled}
        onChange={(e: CheckboxChangeParams) => onIsCameChange(e, rowData)}
        checked={rowData.reservationStatusId === 2}
        style={{ cursor: rowData.reservationStatusId === 3 ? 'not-allowed' : '' }}></Checkbox>
    )
  }

  const isCancelBodyTemplate = (rowData: ICampusServiceReservations) => {
    return (
      <TableTextButton
        style={{ cursor: rowData.reservationStatusId === 2 ? 'not-allowed' : '' }}
        disabled={rowData.reservationStatusId === 2}
        className="p-button-text p-button-plain"
        onClick={() => {
          setNotification(true)
          setSelectedRow(rowData)
        }}>
        {t('buttons.cancel2')}
      </TableTextButton>
    )
  }

  const panelHeaderTemplate = (options: any) => {
    if (options) {
      return (
        <div className="d-flex p-multiselect-header">
          <span>
            {options.checkboxElement} <label className="px-2">{t('buttons.choose-all')}</label>
          </span>
          <div>{options.closeElement}</div>
        </div>
      )
    }
  }

  const blockFilterTemplate = (options: { value: any[] | undefined; filterCallback: (arg0: any) => void }) => {
    return (
      <MultiSelect
        value={options.value}
        options={(tabIndex === 0 ? activeBlockList : canceledBlockList) || []}
        optionLabel="name"
        panelHeaderTemplate={panelHeaderTemplate}
        optionValue="name"
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={t('most-used-words.block')}
        className="p-column-filter filter-multiselect"
      />
    )
  }

  const floorFilterTemplate = (options: { value: any[] | undefined; filterCallback: (arg0: any) => void }) => {
    return (
      <MultiSelect
        value={options.value}
        options={(tabIndex === 0 ? activeFloorList : canceledFloorList) || []}
        optionLabel="name"
        optionValue="name"
        panelHeaderTemplate={panelHeaderTemplate}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={t('most-used-words.floor')}
        className="p-column-filter filter-multiselect"
      />
    )
  }

  const visitedFilterTemplate = (options: { checked: any; value: any; filterCallback: (arg0: any) => void }) => {
    return (
      <Dropdown
        optionValue="value"
        optionLabel="label"
        value={options.value}
        options={isCameOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder={t('visitor.situation')}
        className="p-column-filter filter-multiselect"
      />
    )
  }

  const isCancelFilterTemplate = (options: { checked: any; value: any; filterCallback: (arg0: any) => void }) => {
    return (
      <Dropdown
        value={options.value == null ? null : options.value ? t('visitor.came') : t('visitor.not-come')}
        options={isCameOptions}
        onChange={(e) => options.filterCallback(e.value === t('visitor.came'))}
        placeholder={t('visitor.situation')}
        className="p-column-filter filter-multiselect"
      />
    )
  }

  const footerLeftTemplate = () => {
    return (
      <div>
        <span>{t('visitor.between')} </span>
        <span className="font-bold">"{moment(startDate).format('DD/MM/YYYY')}"</span>
        <span> - </span>
        <span className="font-bold"> "{moment(endDate).format('DD/MM/YYYY')}" </span>
        {t('visitor.in-range')}
        <span className="font-bold"> {whichTabData?.length} </span>
        {t('visitor.record')} {t('visitor.record-listed')}
      </div>
    )
  }

  const emptyMessageTemplate = () => {
    return (
      <div className="p-3 d-flex gap-2 flex-column justify-content-center align-items-center">
        <img src={noRecordImg} width={150} height={150} alt="no-record-found" />
        <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#363636' }}>{t('visitor.no-record-found')}</span>
      </div>
    )
  }

  return (
    <div className="mb-4">
      {/* DataTable */}
      <div className="tracing-table">
        <div className="card p-3">
          {/* Table Header - Search Input - Excel Export */}
          {tableHeaderTemplate()}

          <TabView activeIndex={tabIndex} onTabChange={handleTabChange}>
            {tabs.map((tab) => (
              <TabPanel key={tab.key} header={tab.header}>
                {tabIndex === 0 ? (
                  <DataTable
                    rowHover
                    tableStyle={{ fontSize: '14px' }}
                    key={`${tab.key}_table`}
                    id={`${tab.key}_id`}
                    value={activeTableData}
                    loading={loading}
                    filters={filters}
                    emptyMessage={emptyMessageTemplate}
                    globalFilterFields={['_nameSurname']}
                    sortField="reservationStartHour"
                    sortOrder={-1}
                    dataKey="id"
                    paginator={hasItems}
                    first={first}
                    rows={rows}
                    onPage={handlePageChange}
                    rowsPerPageOptions={[5, 10, 20]}
                    paginatorLeft={footerLeftTemplate}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={
                      t('visitor.from') +
                      '{totalRecords}' +
                      ' ' +
                      t('visitor.record') +
                      ' ' +
                      t('visitor.in') +
                      ' {first}. ' +
                      t('visitor.and') +
                      ' {last}. ' +
                      t('visitor.records-shown')
                    }
                    responsiveLayout="scroll">
                    <Column
                      field="_nameSurname"
                      header={`${t('visitor.name')} ${t('visitor.surname')}`}
                      sortable
                      style={{ minWidth: '10rem' }}
                      bodyStyle={{ fontWeight: 'bold' }}
                    />
                    <Column field="employeeEmail" header={t('visitor.email')} style={{ maxWidth: '20rem' }} />
                    <Column field="firmName" header={t('visitor.employee-firm')} style={{ minWidth: '10rem' }} />
                    <Column
                      field="blockName"
                      header={t('visitor.location-block')}
                      style={{ minWidth: '8rem' }}
                      filter
                      showFilterMatchModes={false}
                      showFilterMenuOptions={false}
                      filterElement={blockFilterTemplate}
                    />
                    <Column
                      field="floorName"
                      header={t('visitor.location-floor')}
                      style={{ minWidth: '8rem' }}
                      filter
                      showFilterMatchModes={false}
                      showFilterMenuOptions={false}
                      filterElement={floorFilterTemplate}
                    />
                    <Column
                      sortable
                      align="center"
                      field="reservationStartHour"
                      header={t('input-texts.date')}
                      style={{ maxWidth: '8rem' }}
                      body={dateBodyTemplate}
                    />
                    <Column
                      align="center"
                      field="reservationStartHour"
                      header={t('visitor.appointment-time')}
                      style={{ minWidth: '8rem' }}
                      body={timeBodyTemplate}
                      sortField="reservationEndHour"
                    />
                    <Column
                      align="center"
                      field="campusServiceType"
                      header={t('visitor.res-reason')}
                      style={{ minWidth: '8rem' }}
                      body={reasonBodyTemplate}
                    />
                    <Column
                      align="center"
                      field="campusServiceSubType"
                      header={t('visitor.res-description')}
                      style={{ minWidth: '8rem' }}
                      body={resDescBodyTemplate}
                    />
                    <Column
                      align="center"
                      field="placeOfServiceName"
                      header={t('visitor.res-place')}
                      style={{ minWidth: '8rem' }}
                      body={resPlaceTemplate}
                    />
                    <Column
                      align="center"
                      field="servicePersonName"
                      header={t('visitor.res-person')}
                      style={{ minWidth: '8rem' }}
                      body={resPersonTemplate}
                    />
                    <Column
                      align="center"
                      field="reservationStatusId"
                      header={t('visitor.came')}
                      body={isCameBodyTemplate}
                      filter
                      showFilterMatchModes={false}
                      showFilterMenuOptions={false}
                      filterElement={visitedFilterTemplate}
                      style={{ maxWidth: '5rem' }}
                    />
                    <Column
                      align="center"
                      field="IsCancel"
                      header={
                        <TableTextButton
                          disabled={selectedCancelableRows?.length < 1}
                          width={100}
                          height={45}
                          className="p-button-text p-button-plain py-3"
                          onClick={() => setMultiNotification(true)}>
                          {t('visitor.cancel-reservation')}
                        </TableTextButton>
                      }
                      body={isCancelBodyTemplate}
                      showFilterMatchModes={false}
                      showFilterMenuOptions={false}
                      filterElement={isCancelFilterTemplate}
                      style={{ maxWidth: '8rem' }}
                    />
                  </DataTable>
                ) : (
                  <DataTable
                    rowHover
                    tableStyle={{ fontSize: '14px' }}
                    key={`${tab.key}_table`}
                    id={`${tab.key}_id`}
                    value={canceledTableData}
                    loading={loading}
                    filters={filters}
                    emptyMessage={emptyMessageTemplate}
                    globalFilterFields={['_nameSurname']}
                    sortField="reservationStartHour"
                    sortOrder={-1}
                    dataKey="id"
                    paginator={hasItems}
                    rows={10}
                    rowsPerPageOptions={[5, 10, 15]}
                    paginatorLeft={footerLeftTemplate}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={
                      t('visitor.from') +
                      '{totalRecords}' +
                      ' ' +
                      t('visitor.record') +
                      ' ' +
                      t('visitor.in') +
                      ' {first}. ' +
                      t('visitor.and') +
                      ' {last}. ' +
                      t('visitor.records-shown')
                    }
                    responsiveLayout="scroll">
                    <Column
                      field="_nameSurname"
                      header={`${t('visitor.name')} ${t('visitor.surname')}`}
                      bodyStyle={{ fontWeight: 'bold' }}
                      style={{ minWidth: '10rem' }}
                      sortable
                    />

                    <Column field="employeeEmail" header={t('visitor.email')} style={{ minWidth: '12rem' }} />
                    <Column field="firmName" header={t('visitor.employee-firm')} />
                    <Column
                      field="blockName"
                      header={t('visitor.location-block')}
                      style={{ minWidth: '8rem' }}
                      filter
                      showFilterMatchModes={false}
                      showFilterMenuOptions={false}
                      filterElement={blockFilterTemplate}
                    />
                    <Column
                      field="floorName"
                      header={t('visitor.location-floor')}
                      style={{ minWidth: '8rem' }}
                      filter
                      showFilterMatchModes={false}
                      showFilterMenuOptions={false}
                      filterElement={floorFilterTemplate}
                    />
                    <Column
                      sortable
                      style={{ minWidth: '6rem' }}
                      align="center"
                      field="reservationStartHour"
                      header={t('input-texts.date')}
                      body={dateBodyTemplate}
                    />
                    <Column
                      style={{ minWidth: '8rem' }}
                      align="center"
                      field="reservationStartHour"
                      header={t('visitor.appointment-time')}
                      body={timeBodyTemplate}
                    />
                    <Column
                      align="center"
                      field="campusServiceType"
                      header={t('visitor.res-reason')}
                      style={{ minWidth: '8rem' }}
                      body={reasonBodyTemplate}
                    />
                    <Column
                      align="center"
                      field="campusServiceSubType"
                      header={t('visitor.res-description')}
                      style={{ minWidth: '8rem' }}
                      body={resDescBodyTemplate}
                    />
                    <Column
                      align="center"
                      field="placeOfServiceName"
                      header={t('visitor.res-place')}
                      style={{ minWidth: '8rem' }}
                      body={resPlaceTemplate}
                    />
                    <Column
                      align="center"
                      field="servicePersonName"
                      header={t('visitor.res-person')}
                      style={{ minWidth: '8rem' }}
                      body={resPersonTemplate}
                    />
                    <Column
                      sortable
                      align="center"
                      field="statusUpdateDate"
                      header={t('input-texts.cancel-date')}
                      style={{ minWidth: '12rem' }}
                      body={cancelDateBodyTemplate}
                    />
                  </DataTable>
                )}
              </TabPanel>
            ))}
          </TabView>

          <NotificationDialog
            display={notification}
            imgSrc={warningImage}
            goHomeButton
            extraButton
            title={t('overlaypanel-texts.cancel-process')}
            content={t('overlaypanel-texts.cancel-reservation')}
            firstButtonLabel={t('buttons.yes')}
            secondButtonLabel={t('buttons.no')}
            onHide={() => setNotification(false)}
            onSubmit={() => handleConfirmIsCanceled(selectedRow)}
            selectedType={null}
            setSelectedType={undefined}
            types={[]}
          />

          <NotificationDialog
            display={multiNotification}
            imgSrc={warningImage}
            goHomeButton
            extraButton
            title={t('overlaypanel-texts.cancel-all-process')}
            content={t('overlaypanel-texts.cancel-all-reservations', {
              reservationCount: selectedCancelableRows?.length,
            })}
            firstButtonLabel={t('buttons.yes')}
            secondButtonLabel={t('buttons.no')}
            onHide={() => setMultiNotification(false)}
            onSubmit={handleConfirmMultiIsCanceled}
            selectedType={null}
            setSelectedType={undefined}
            types={[]}
          />
        </div>
      </div>
    </div>
  )
}
